import { memo, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormState, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import Input from '@mui/material/Input'
import InputBase from '@mui/material/InputBase'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Typography from '@mui/material/Typography'
import { useDebouncedState } from '@react-hookz/web'
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table'

import { api } from '~/api'
import Section from '~/components/Section'
import { useAppSelector } from '~/store'
import type { GiftWithPurchaseControl } from '../hooks'
import { giftWithPurchaseQueryKeys } from '../queries/queryKeys'
import type { Gift } from '../schemas'

const DEFAULT_TIER_LIMIT = 6
const FIELD_MAX_WIDTH = 64

type TierFieldProps = {
    index: number
    control: GiftWithPurchaseControl
}

type TierThresholdProps = TierFieldProps & {
    canRemove: boolean
    onRemove: () => void
}

function TierThreshold({ control, canRemove, index, onRemove }: TierThresholdProps) {
    const { t } = useTranslation('gift_with_purchase')

    return (
        <div>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                    <Typography fontWeight={700}>
                        {t('form.tiers.current_tier', { tier: index + 1 })}
                    </Typography>
                    <Controller
                        control={control}
                        name={`tiers.${index}.campaign_type`}
                        render={({ field }) => (
                            <ToggleButtonGroup {...field} exclusive color="secondary" size="small">
                                <ToggleButton
                                    value="spend"
                                    sx={{ fontSize: 14, textTransform: 'capitalize' }}
                                >
                                    {t('form.campaign_info.field_type_spend')}
                                </ToggleButton>
                                <ToggleButton
                                    value="items"
                                    sx={{ fontSize: 14, textTransform: 'capitalize' }}
                                >
                                    {t('form.campaign_info.field_type_items')}
                                </ToggleButton>
                            </ToggleButtonGroup>
                        )}
                    />
                </Stack>
                {canRemove && (
                    <IconButton onClick={onRemove}>
                        <DeleteIcon />
                    </IconButton>
                )}
            </Stack>
        </div>
    )
}

function TierRedemptions({ control, index }: TierFieldProps) {
    const { t } = useTranslation('gift_with_purchase')
    const tierId = useWatch({ control, name: `tiers.${index}.id` })
    const numRedemptions = useWatch({ control, name: `tiers.${index}.num_redeemed` })
    const type = useWatch({ control, name: `tiers.${index}.campaign_type` })
    const isNewTier = !tierId

    return (
        <Stack gap={0.5}>
            <Controller
                key={type}
                control={control}
                name={
                    type === 'spend'
                        ? `tiers.${index}.spend_threshold`
                        : `tiers.${index}.num_items_threshold`
                }
                render={({ field, fieldState }) => (
                    <Stack direction="row" justifyContent="space-between">
                        <div>
                            <Typography variant="subtitle1">
                                {type === 'spend'
                                    ? t('form.tiers.spend_at_least')
                                    : t('form.tiers.buy_at_least')}
                            </Typography>
                            {fieldState.error && (
                                <Typography variant="caption" color="error">
                                    {fieldState.error.message}
                                </Typography>
                            )}
                        </div>
                        <TextField
                            {...field}
                            sx={{ maxWidth: FIELD_MAX_WIDTH * 2 }}
                            color="secondary"
                            type="number"
                            variant="outlined"
                            size="small"
                            error={!!fieldState.error}
                            InputProps={{
                                slotProps: {
                                    input: {
                                        min: 0,
                                        step: type === 'spend' ? 0.01 : 1,
                                        sx: {
                                            textAlign: 'right',
                                            '&::-webkit-outer-spin-button': {
                                                appearance: 'none',
                                            },
                                            '&::-webkit-inner-spin-button': {
                                                appearance: 'none',
                                            },
                                            '&::-moz-inner-spin-button': {
                                                appearance: 'none',
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                    </Stack>
                )}
            />
            <Controller
                control={control}
                name={`tiers.${index}.max_redemptions`}
                render={({ field, fieldState }) => (
                    <Stack direction="row" justifyContent="space-between">
                        <div>
                            <Typography variant="subtitle1">
                                {t('form.tiers.redemption_max')}
                            </Typography>
                            {fieldState.error && (
                                <Typography variant="caption" color="error">
                                    {fieldState.error.message}
                                </Typography>
                            )}
                        </div>
                        <TextField
                            {...field}
                            color="secondary"
                            type="number"
                            variant="outlined"
                            size="small"
                            disabled={!isNewTier}
                            error={!!fieldState.error}
                            InputProps={{
                                slotProps: {
                                    input: {
                                        min: 0,
                                        sx: {
                                            textAlign: 'right',
                                            maxWidth: FIELD_MAX_WIDTH,
                                            '&::-webkit-outer-spin-button': {
                                                appearance: 'none',
                                            },
                                            '&::-webkit-inner-spin-button': {
                                                appearance: 'none',
                                            },
                                            '&::-moz-inner-spin-button': {
                                                appearance: 'none',
                                            },
                                        },
                                    },
                                },
                            }}
                        />
                    </Stack>
                )}
            />
            {!isNewTier && (
                <Stack direction="row" justifyContent="space-between">
                    <Typography variant="caption" color="text.secondary">
                        {t('form.tiers.redemption_count')}
                    </Typography>
                    <InputBase
                        disabled
                        type="number"
                        size="small"
                        value={numRedemptions ?? '0'}
                        slotProps={{
                            input: {
                                sx: {
                                    maxWidth: FIELD_MAX_WIDTH,
                                    textAlign: 'right',
                                    color: 'text.secondary',
                                },
                            },
                        }}
                    />
                </Stack>
            )}
            <Stack direction="row" justifyContent="space-between">
                <Controller
                    control={control}
                    name={`tiers.${index}.limit_per_customer`}
                    render={({ field, fieldState }) => (
                        <>
                            <div>
                                <Typography variant="subtitle1">
                                    {t('form.tiers.customer_limit')}
                                </Typography>
                                {fieldState.error && (
                                    <Typography variant="caption" color="error">
                                        {fieldState.error.message}
                                    </Typography>
                                )}
                            </div>
                            <TextField
                                {...field}
                                color="secondary"
                                type="number"
                                variant="outlined"
                                size="small"
                                error={!!fieldState.error}
                                InputProps={{
                                    readOnly: !isNewTier,
                                    slotProps: {
                                        input: {
                                            min: 0,
                                            sx: {
                                                textAlign: 'right',
                                                maxWidth: FIELD_MAX_WIDTH,
                                                '&::-webkit-outer-spin-button': {
                                                    appearance: 'none',
                                                },
                                                '&::-webkit-inner-spin-button': {
                                                    appearance: 'none',
                                                },
                                                '&::-moz-inner-spin-button': {
                                                    appearance: 'none',
                                                },
                                            },
                                        },
                                    },
                                }}
                            />
                        </>
                    )}
                />
            </Stack>
        </Stack>
    )
}

type TierGiftsDialogProps = {
    open: boolean
    existingIds?: number[]
    onClose: () => void
    onAdd: (items: Omit<Gift, 'id'>[]) => void
}

function TierGiftsDialog({ open, existingIds, onAdd, onClose }: TierGiftsDialogProps) {
    const { t } = useTranslation(['common', 'gift_with_purchase'])
    const accountOwnerId = useAppSelector((state) => state.initial.accountOwner.id)

    const [search, setSearch] = useDebouncedState('', 500)
    const enabled = open && !!accountOwnerId

    const excludedIds = useMemo(() => existingIds?.join(',') ?? '', [existingIds])

    const { data, status } = api.stock.stockItems.useQuery(
        giftWithPurchaseQueryKeys.tierGiftsSearch({ search, excludedIds }),
        {
            query: {
                search,
                id_not_in: excludedIds,
            },
        },
        { enabled }
    )

    const table = useReactTable({
        getCoreRowModel: getCoreRowModel(),
        data: data?.body.results ?? [],
        columns: [
            {
                id: 'select',
                header: ({ table }) => (
                    <Checkbox
                        color="secondary"
                        disabled={status === 'loading' || table.getRowCount() === 0}
                        indeterminate={table.getIsSomeRowsSelected()}
                        checked={table.getIsAllRowsSelected()}
                        onChange={(_, checked) => table.toggleAllRowsSelected(checked)}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        color="secondary"
                        checked={row.getIsSelected()}
                        onChange={(_, checked) => row.toggleSelected(checked)}
                    />
                ),
            },
            {
                accessorKey: 'cover_image_url',
                header: '',
                cell: ({ getValue }) => <img src={getValue()} style={{ width: 40 }} />,
            },
            { accessorKey: 'name', header: t('common:name') },
            { accessorKey: 'sku', header: 'SKU' },
        ],
    })

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            TransitionProps={{
                onExited: () => {
                    table.resetRowSelection()
                    setSearch('')
                },
            }}
            onClose={onClose}
        >
            <DialogTitle>{t('gift_with_purchase:form.tiers.add_gift')}</DialogTitle>
            <DialogContent>
                <Stack gap={4}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={t('common:search')}
                        color="secondary"
                        InputProps={{
                            slotProps: {
                                input: {
                                    sx: {
                                        '&::placeholder': {
                                            textTransform: 'capitalize',
                                        },
                                    },
                                },
                            },
                            endAdornment:
                                enabled && status === 'loading' ? (
                                    <CircularProgress size={16} />
                                ) : null,
                        }}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <Table size="small">
                        <TableHead>
                            {table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id}>
                                    {headerGroup.headers.map((header) => (
                                        <TableCell
                                            key={header.id}
                                            sx={{ textTransform: 'capitalize' }}
                                            padding={
                                                header.id === 'select' ? 'checkbox' : undefined
                                            }
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext()
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableHead>
                        <TableBody>
                            {status === 'loading' &&
                                (enabled ? (
                                    Array.from({ length: 5 }).map((_, index) => (
                                        <TableRow key={index}>
                                            {table.getFlatHeaders().map((header) => (
                                                <TableCell
                                                    key={header.id}
                                                    padding={
                                                        header.id === 'select'
                                                            ? 'checkbox'
                                                            : undefined
                                                    }
                                                >
                                                    {header.id === 'select' ? (
                                                        <Checkbox disabled />
                                                    ) : (
                                                        <Skeleton variant="text" />
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            sx={{ py: 2 }}
                                            align="center"
                                            colSpan={table.getFlatHeaders().length}
                                        >
                                            {t('gift_with_purchase:form.tiers.search_for_gift')}
                                        </TableCell>
                                    </TableRow>
                                ))}

                            {status === 'success' && table.getRowCount() === 0 ? (
                                <TableRow>
                                    <TableCell
                                        sx={{ py: 2 }}
                                        align="center"
                                        colSpan={table.getFlatHeaders().length}
                                    >
                                        {t('common:no_results')}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                table.getRowModel().rows.map((row) => (
                                    <TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (
                                            <TableCell
                                                key={cell.id}
                                                padding={
                                                    cell.column.columnDef.id === 'select'
                                                        ? 'checkbox'
                                                        : undefined
                                                }
                                            >
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button sx={{ color: 'text.secondary' }} onClick={onClose}>
                    {t('common:cancel')}
                </Button>
                <Button
                    disableElevation
                    variant="contained"
                    disabled={!table.getSelectedRowModel().rows.length}
                    onClick={() => {
                        const updatedRows = table.getSelectedRowModel().rows.map((r) => ({
                            stock_item_id: r.original.id,
                            stock_item: {
                                id: r.original.id,
                                name: r.original.name,
                                cover_image_url: r.original.cover_image_url,
                            },
                            quantity: 1,
                        }))

                        onAdd(updatedRows)
                    }}
                >
                    {t('common:add')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

function TierGifts({ control, index }: TierFieldProps) {
    const { t } = useTranslation('gift_with_purchase')
    const gifts = useFieldArray({ control, name: `tiers.${index}.gifts`, keyName: 'keyId' })
    const hasGifts = gifts.fields.length > 0
    const existingIds = useMemo(() => gifts.fields.map((g) => g.stock_item.id), [gifts.fields])

    const formState = useFormState({ control, name: `tiers.${index}.gifts`, exact: true })
    const error = formState.errors.tiers?.[index]?.gifts?.message

    const [open, setOpen] = useState(false)

    return (
        <div>
            <Typography gutterBottom={!error} variant="subtitle1">
                {t('form.tiers.gifts')}
            </Typography>
            {!!error && (
                <Typography gutterBottom variant="caption" color="error">
                    {error}
                </Typography>
            )}
            <Stack gap={1}>
                {gifts.fields.map((gift, giftIndex) => (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        key={gift.keyId}
                        gap={1}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            gap={2}
                        >
                            <img src={gift.stock_item.cover_image_url ?? undefined} height={30} />
                            <Typography sx={{ wordBreak: 'break-all' }}>
                                {gift.stock_item.name}
                            </Typography>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                            <Controller
                                control={control}
                                name={`tiers.${index}.gifts.${giftIndex}.quantity`}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        type="number"
                                        slotProps={{
                                            input: {
                                                min: 1,
                                                sx: {
                                                    textAlign: 'right',
                                                    maxWidth: FIELD_MAX_WIDTH,
                                                },
                                            },
                                        }}
                                    />
                                )}
                            />
                            <IconButton size="small" onClick={() => gifts.remove(giftIndex)}>
                                <DeleteIcon fontSize="inherit" />
                            </IconButton>
                        </Stack>
                    </Stack>
                ))}
                <Button
                    color="inherit"
                    variant="text"
                    size="small"
                    startIcon={<AddIcon />}
                    sx={{ mt: hasGifts ? 2 : 0 }}
                    onClick={() => setOpen(true)}
                >
                    {t('form.tiers.add_gift')}
                </Button>
            </Stack>

            <TierGiftsDialog
                open={open}
                existingIds={existingIds}
                onAdd={(items) => {
                    gifts.append(items)
                    setOpen(false)
                }}
                onClose={() => {
                    setOpen(false)
                }}
            />
        </div>
    )
}

type Props = {
    control: GiftWithPurchaseControl
    tierLimit?: number
}

export default memo(function SpendingTiers({ control, tierLimit = DEFAULT_TIER_LIMIT }: Props) {
    const { t } = useTranslation('gift_with_purchase')
    const tiers = useFieldArray({ control, name: 'tiers', keyName: 'keyId' })
    const isMaxLimitReached = tiers.fields.length >= tierLimit

    return (
        <Section
            header={<Section.Header label={t('form.tiers.title')} />}
            sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            }}
        >
            {tiers.fields.map((tier, index) => (
                <Stack
                    key={tier.keyId}
                    component={Paper}
                    elevation={0}
                    sx={{ gap: 2, p: 3, border: '1px solid', borderColor: 'divider' }}
                >
                    <TierThreshold
                        control={control}
                        index={index}
                        canRemove={tiers.fields.length > 1}
                        onRemove={() => tiers.remove(index)}
                    />
                    <TierRedemptions control={control} index={index} />
                    <Divider />
                    <TierGifts control={control} index={index} />
                </Stack>
            ))}
            {!isMaxLimitReached && (
                <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <Button
                        color="inherit"
                        startIcon={<AddIcon />}
                        onClick={() => {
                            tiers.append({
                                gifts: [],
                                limit_per_customer: 0,
                                max_redemptions: 0,
                                num_items_threshold: 0,
                                num_redeemed: 0,
                                spend_threshold: 0,
                            })
                        }}
                    >
                        {t('form.tiers.add_tier')}
                    </Button>
                </Box>
            )}
        </Section>
    )
})
